import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAvailableCategoryFieldsToAdd, selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import EditableDeviceField from 'zComponents/molecules/EditableDeviceField';
import { Button, Col, Flex, Form, Row, Select, Tooltip } from 'zui/Antd';

const AddDeviceFields = ({ deviceID, setAddField, setIsEditingAdditionalInfo }) => {
	// Hooks
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const {
		token: { marginMD, fontSize, colorTextDescription, paddingMD }
	} = theme.useToken();

	// Local state
	const [fields, setFields] = useState([{ fieldID: undefined, value: undefined }]);
	const [selectedFields, setSelectedFields] = useState({});

	// Redux state
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const availableFields = useSelector(selectAvailableCategoryFieldsToAdd(deviceID, device?.categoryID));
	const { createDeviceFieldLoading } = useSelector((state) => state.Devices);

	// Disable submit if any field is incomplete
	const isSubmitDisabled = useMemo(() => {
		return fields.some((field) => !field.fieldID || field.value === undefined || field.value === '');
	}, [fields]);

	// Add new empty field to state
	const handleAdd = () => {
		setFields([...fields, { fieldID: undefined, value: undefined }]);
	};

	// Remove field from state
	const handleRemove = (index) => {
		if (fields.length === 1) {
			handleCancelAdd();
			return;
		}
		const updated = [...fields];
		updated.splice(index, 1);
		setFields(updated);
	};

	// Update field state and track selected fields to filter options
	const handleFieldChange = (index, key, value) => {
		const updated = [...fields];
		updated[index][key] = value;
		setFields(updated);

		// ✅ Update form values to keep in sync with local state
		form.setFieldsValue({ fields: updated });

		if (key === 'fieldID') {
			setSelectedFields((prev) => ({ ...prev, [index]: value }));
		}
	};

	// Reset state and close the add field panel
	const handleCancelAdd = () => {
		setFields([{ fieldID: undefined, value: undefined }]);
		setSelectedFields({});
		setAddField(false);
		setIsEditingAdditionalInfo(false);
	};

	// Create new fields
	const handleSubmit = ({ fields }) => {
		const validNewFields = fields.filter((f) => f.fieldID && f.value != null && f.value !== '');
		if (validNewFields.length) {
			dispatch(devicesActions.createDeviceField({ deviceID, data: { fields: validNewFields } }));
		}
		handleCancelAdd();
	};

	// Determine if more fields can be added
	const canAddMoreFields = fields.length < availableFields.length;

	return (
		<Form form={form} layout='vertical' onFinish={handleSubmit}>
			{fields.map((fieldItem, index) => {
				const selectedFieldID = selectedFields[index] || fieldItem.fieldID;
				const categoryField = availableFields.find((f) => f.fieldID === selectedFieldID);

				const filteredOptions = availableFields
					.filter((f) => !fields.some((item, i) => i !== index && item.fieldID === f.fieldID))
					.map((f) => ({ label: f.title, value: f.fieldID }));

				return (
					<Row key={index} gutter={[paddingMD, paddingMD]} style={{ marginBottom: marginMD }} align='middle'>
						<Col xs={24} md={12}>
							<Form.Item
								name={['fields', index, 'fieldID']}
								rules={[{ required: true, message: 'Please select a field' }]}
								style={{ marginBottom: 0 }}
							>
								<Select
									placeholder='Select Field'
									options={filteredOptions}
									value={fieldItem.fieldID}
									onChange={(value) => handleFieldChange(index, 'fieldID', value)}
								/>
							</Form.Item>
						</Col>

						<Col style={{ position: 'relative' }} xs={24} md={12}>
							<Flex justify='space-between' align='center'>
								<Form.Item
									name={['fields', index, 'value']}
									rules={[{ required: true, message: 'Please enter a value' }]}
									style={{ marginBottom: 0, flex: 1 }}
								>
									<EditableDeviceField
										fieldTypeID={categoryField?.fieldTypeID}
										value={fieldItem.value}
										onChange={(value) => handleFieldChange(index, 'value', value)}
										onChangeInput={(value) => handleFieldChange(index, 'value', value)}
										disabled={createDeviceFieldLoading}
									/>
								</Form.Item>
								<Tooltip title='Remove Field'>
									<Button
										type='text'
										icon={
											<CloseOutlined
												style={{ fontSize: fontSize, color: colorTextDescription }}
											/>
										}
										onClick={() => handleRemove(index)}
										disabled={createDeviceFieldLoading}
									/>
								</Tooltip>
							</Flex>
						</Col>
					</Row>
				);
			})}

			{canAddMoreFields && (
				<Form.Item>
					<Button type='dashed' onClick={handleAdd} block icon={<PlusOutlined />}>
						Add Field
					</Button>
				</Form.Item>
			)}

			<Flex justify='flex-end' align='center' style={{ marginTop: marginMD, marginBottom: marginMD }} gap={8}>
				<Button onClick={handleCancelAdd} disabled={createDeviceFieldLoading}>
					Cancel
				</Button>
				<Button
					type='primary'
					htmlType='submit'
					disabled={isSubmitDisabled || createDeviceFieldLoading}
					loading={createDeviceFieldLoading}
				>
					Add Fields
				</Button>
			</Flex>
		</Form>
	);
};

export default AddDeviceFields;
